import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

export default function EyeButton({ onClick, text = "" }) {
  const eyeRef = useRef(null);
  const [pupilPos, setPupilPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!eyeRef.current) return;

      // 1. Obtener las coordenadas del ojo en la pantalla
      const eyeRect = eyeRef.current.getBoundingClientRect();
      
      // 2. Calcular el centro exacto del ojo
      const eyeCenterX = eyeRect.left + eyeRect.width / 2;
      const eyeCenterY = eyeRect.top + eyeRect.height / 2;

      // 3. Posición del cursor del mouse
      const mouseX = event.clientX;
      const mouseY = event.clientY;

      // 4. Calcular la distancia y el ángulo
      const deltaX = mouseX - eyeCenterX;
      const deltaY = mouseY - eyeCenterY;
      const angle = Math.atan2(deltaY, deltaX);
      
      // Calcular la distancia real del mouse al ojo
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // 5. Limitar el movimiento de la pupila (para que no se salga del ojo blanco)
      // distance / 20 hace que se mueva suavemente, y Math.min(..., 10) pone un tope de 10px de radio.
      const maxPupilRadius = 10; 
      const radius = Math.min(distance / 15, maxPupilRadius);

      // 6. Aplicar la nueva posición X y Y a la pupila
      const pupilX = Math.cos(angle) * radius;
      const pupilY = Math.sin(angle) * radius;

      setPupilPos({ x: pupilX, y: pupilY });
    };

    // Escuchar el movimiento del mouse en toda la ventana
    window.addEventListener('mousemove', handleMouseMove);
    
    // Limpiar el evento cuando el componente se desmonte
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <Box 
      onClick={onClick}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        ml:2,
        cursor: 'pointer',
        // Efecto hover para el texto
        '&:hover .eye-text': {
          color: '#aaa', // Cambia el color del texto al pasar el mouse
        }
      }}
    >
      {/* Contenedor del Ojo (Forma de Almendra/Esclerótica) */}
      <Box
        ref={eyeRef}
        sx={{
          width: 60,
          height: 60,
          backgroundColor: '#fff',
          // Este borderRadius crea la forma de un ojo en lugar de un círculo perfecto
          borderRadius: '75% 15%',
          transform: 'rotate(45deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0px 0px 20px rgba(255,255,255,0.2)',
          transition: 'box-shadow 0.3s ease, transform 0.3s ease',
          overflow: 'hidden', // Evita que la pupila pinte fuera de los bordes si cambias el tamaño
          '&:hover': {
            boxShadow: '0px 0px 30px rgba(255,255,255,0.6)',
            transform: 'rotate(45deg) scale(1.1)' // Crece un poco al hacer hover
          }
        }}
      >
        {/* La Pupila (Negra) */}
        <Box
          sx={{
            width: 24,
            height: 24,
            backgroundColor: '#000',
            borderRadius: '50%',
            // Deshacemos la rotación de 45 grados del contenedor padre para que la matemática (X,Y) funcione derecha
            transform: `rotate(-45deg) translate(${pupilPos.x}px, ${pupilPos.y}px)`,
            // Usamos un transition muy corto para que se vea natural pero fluido
            transition: 'transform 0.05s linear',
          }}
        />
      </Box>

      {/* Texto al lado del ojo (Opcional, lo puedes quitar si solo quieres el icono) */}
      <Box 
        className="eye-text"
        sx={{ 
          color: '#fff', 
          fontWeight: 'bold', 
          fontSize: '1.2rem',
          transition: 'color 0.3s'
        }}
      >
        {text}
      </Box>
    </Box>
  );
}